import React from "react";
import { Trophy, Calendar, Eye, Lock, Coins, Users, MapPin } from "lucide-react";

/** Live preview of how a tournament/league card will render on the public
 *  directory page. Mirrors the styling in `pages/Tournaments.jsx` and
 *  `pages/Leagues.jsx` so what the organizer sees is what players see.
 *
 *  Props:
 *    kind:  "tournament" | "league"
 *    data:  full form state (tform / lform)
 *    sport: hydrated sport object {name, slug} (optional; falls back to placeholder)
 */
export default function EventCardPreview({ kind, data, sport }) {
  const isTournament = kind === "tournament";
  // Brand colour drawn from the cover image (or organizer override) takes
  // precedence over the sport-default accent. Falls back to the sport accent
  // (blue for tournaments, green for leagues) when nothing has been set.
  const sportAccent = isTournament ? "#007AFF" : "#00E676";
  const accent = data?.brand_color || sportAccent;
  const sportName = sport?.name || "Sport";
  const isPrivate = data?.visibility === "private";
  const allDay = !!data?.all_day;

  const start = data?.start_date ? new Date(data.start_date) : null;
  const startLabel = start && !isNaN(start.getTime())
    ? (allDay
        ? start.toLocaleDateString()
        : start.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }))
    : "Pick a date";

  // For tournaments, derive a representative entry fee from the cheapest class
  // when classes are in play (matches what the directory list eventually shows
  // via the `entry_fee` field on tournament docs). Golf tournaments fall back to
  // the explicit `entry_fee` input.
  let priceLabel = "Free";
  let currency = data?.currency || "EUR";
  if (isTournament) {
    if (sport?.slug === "golf") {
      const fee = Number(data?.entry_fee || 0);
      priceLabel = fee > 0 ? `${fee} ${currency}` : "Free";
    } else if (data?.classes && data.classes.length > 0) {
      const prices = data.classes.map((c) => Number(c.price || 0)).filter((n) => n > 0);
      if (prices.length > 0) {
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        currency = data.classes[0]?.currency || currency;
        priceLabel = min === max ? `${min} ${currency}` : `from ${min} ${currency}`;
      }
    }
  } else {
    const fee = Number(data?.entry_fee || 0);
    priceLabel = fee > 0 ? `${fee} ${currency}` : "Free";
  }

  // Tournament status chip — directory cards show `tour.status`. New tournaments
  // are open by default.
  const status = isTournament ? "open" : null;

  // Class count + rough capacity sum gives players a tactile sense of what
  // they're about to see when classes are configured.
  const classCount = isTournament ? (data?.classes?.length || 0) : 0;
  const totalCapacity = isTournament
    ? (data?.classes || []).reduce((sum, c) => sum + (Number(c.capacity) || 0), 0)
    : 0;

  const title = (data?.title || "").trim() || "Your tournament title";
  const description = (data?.description || "").trim() || "Your description appears here.";
  const location = (data?.location || "").trim();

  return (
    <div className="pp-card-elevated p-5 space-y-3" data-testid="event-card-preview">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
          <Eye size={10} /> Live preview
        </span>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">
          {isTournament ? "Tournament" : "League"}
        </span>
      </div>

      {/* Card mirrors the public directory cell. Wrapped in pp-card so the
          spacing/border match what players see exactly. */}
      <div className="pp-card p-6 block" data-testid="event-card-preview-cell">
        {data?.cover_url && (
          <div className="-mx-6 -mt-6 mb-3 rounded-t-2xl overflow-hidden">
            <img src={data.cover_url} alt="cover" className="w-full h-32 object-cover" />
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-wider flex items-center gap-1" style={{ color: accent }}>
            <Trophy size={14} /> {sportName}
          </div>
          <div className="flex items-center gap-1.5">
            {isPrivate && (
              <span className="pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40 flex items-center gap-1">
                <Lock size={10} /> Private
              </span>
            )}
            {status && <span className="pp-chip">{status}</span>}
          </div>
        </div>
        <div className="font-heading text-xl font-bold mt-2" data-testid="event-card-preview-title">{title}</div>
        <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{description}</p>
        <div className="mt-4 flex items-center justify-between text-xs text-zinc-500 flex-wrap gap-2">
          <span className="flex items-center gap-1"><Calendar size={12} /> {startLabel}</span>
          <span className="font-semibold flex items-center gap-1" style={{ color: accent }}>
            <Coins size={12} /> {priceLabel}
          </span>
        </div>
        {(classCount > 0 || location) && (
          <div className="mt-2 flex items-center justify-between text-xs text-zinc-500 flex-wrap gap-2">
            {location && (
              <span className="flex items-center gap-1"><MapPin size={12} /> {location}</span>
            )}
            {classCount > 0 && (
              <span className="flex items-center gap-1" data-testid="event-card-preview-classes">
                <Users size={12} /> {classCount} {classCount === 1 ? "class" : "classes"}
                {totalCapacity > 0 ? ` · ${totalCapacity} seats` : ""}
              </span>
            )}
          </div>
        )}
      </div>

      <p className="text-[10px] text-zinc-500">
        This is exactly how your event will appear on the public {isTournament ? "Tournaments" : "Leagues"} directory.
      </p>
    </div>
  );
}
